<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <v-app>
    <!-- Top Navigation Bar -->
    <v-app-bar :color="colors.primaryDark" dark elevation="4" height="60">
      <div class="d-flex align-center">
        <v-icon size="40" class="ml-7 mr-2 pulse">fas fa-building</v-icon>
        <span class="text-h6 font-weight-bold gradient-text">
          SICON PAINEL DE CONTROLE I.A
        </span>
      </div>
      <v-spacer />
      <div class="d-flex align-center" style="gap: 24px">
        <div class="d-flex align-center mr-12 user-profile" style="gap: 7px">
          <v-avatar size="40" :color="colors.primaryLight" class="profile-avatar">
            <v-icon color="white" size="20">fas fa-user</v-icon>
          </v-avatar>
          <div class="d-flex flex-column">
            <span class="font-weight-bold text-body-2">
              {{ userDetails?.name || 'Carregando...' }}
            </span>
            <span class="text-caption opacity-75">
              {{ userDetails?.department?.name || 'Online' }}
            </span>
          </div>
        </div>
      </div>
    </v-app-bar>

    <!-- Sidebar Navigation -->
    <v-navigation-drawer
      v-model="drawer"
      :color="colors.primaryDark"
      dark
      permanent
      width="250"
      class="custom-drawer"
    >
      <div class="pa-4 sidebar-profile" style="border-bottom: 1px solid #1a4b7a">
        <div class="d-flex align-center" style="gap: 12px">
          <v-avatar size="45" :color="colors.primaryLight" class="profile-avatar">
            <v-icon color="white" size="22">fas fa-user</v-icon>
          </v-avatar>
          <div>
            <div class="font-weight-bold text-body-2 lowercase">
              {{ userDetails?.name || 'Carregando...' }}
            </div>
            <div class="text-caption opacity-75">
              {{ userDetails?.department?.name || 'Sistema Online' }}
            </div>
          </div>
        </div>
      </div>

      <v-list nav class="pa-0 text-start">
        <v-list-item
          v-for="item in menuItems"
          :key="item.title"
          :value="item.value"
          :active="item.active"
          rounded="xl"
          class="mb-2 menu-item"
          :class="item.active ? 'active-item' : ''"
          density="comfortable"
          @click="router.push(item.route)"
        >
          <template #prepend>
            <v-icon size="20" class="mr-2">{{ item.icon }}</v-icon>
          </template>
          <template #title>
            <span class="item-title">{{ item.title }}</span>
          </template>
          <template #append v-if="item.badge">
            <v-chip size="x-small" :color="item.badgeColor" class="pulse-badge">
              {{ item.badge }}
            </v-chip>
          </template>
        </v-list-item>
      </v-list>

      <template #append>
        <div class="pa-3" style="border-top: 1px solid #1a4b7a">
          <v-btn
            block
            variant="outlined"
            prepend-icon="fas fa-sign-out-alt"
            class="logout-btn"
            @click="logout"
          >
            Sair
          </v-btn>
        </div>
      </template>
    </v-navigation-drawer>

    <!-- Main Content -->
    <v-main class="main-content">
      <div class="payment-scheduler pa-6">
        <!-- Module Header -->
        <v-card class="module-header mb-6" elevation="0">
          <div class="d-flex align-center justify-space-between flex-wrap gap-16">
            <div>
              <div class="d-flex align-center mb-2">
                <v-icon size="28" class="mr-3" :color="colors.primaryDark">
                  fas fa-calendar-alt
                </v-icon>
                <h1 class="text-h5 font-weight-bold primary-text">
                  Agendamento de Pagamentos
                </h1>
              </div>
              <p class="text-body-2 text-grey-darken-1 mb-0">
                Selecione os PDFs e confirme o agendamento para processamento no Sicoob.
              </p>
            </div>

            <div class="d-flex align-center" style="gap: 12px">
              <v-btn
                variant="outlined"
                :color="colors.primaryDark"
                prepend-icon="fas fa-rotate-right"
                class="btn-soft"
                @click="refreshFiles"
              >
                Atualizar
              </v-btn>

              <v-btn
                :color="colors.primaryLight"
                prepend-icon="fas fa-lock"
                class="btn-primary"
                @click="openReauth"
              >
                Verificar senha
              </v-btn>
            </div>
          </div>

          <!-- Summary Cards -->
          <v-row class="mt-5" dense>
            <v-col cols="12" md="4">
              <v-card class="kpi-card" elevation="0">
                <div class="d-flex align-center justify-space-between">
                  <div>
                    <div class="text-caption text-grey-darken-1">Pendentes</div>
                    <div class="text-h6 font-weight-bold">{{ pendingCount }}</div>
                  </div>
                  <v-icon size="28" color="orange">fas fa-clock</v-icon>
                </div>
              </v-card>
            </v-col>

            <v-col cols="12" md="4">
              <v-card class="kpi-card" elevation="0">
                <div class="d-flex align-center justify-space-between">
                  <div>
                    <div class="text-caption text-grey-darken-1">Processados</div>
                    <div class="text-h6 font-weight-bold">{{ processedCount }}</div>
                  </div>
                  <v-icon size="28" :color="colors.primaryGreen">fas fa-check-circle</v-icon>
                </div>
              </v-card>
            </v-col>

            <v-col cols="12" md="4">
              <v-card class="kpi-card" elevation="0">
                <div class="d-flex align-center justify-space-between">
                  <div>
                    <div class="text-caption text-grey-darken-1">Total (pendentes)</div>
                    <div class="text-h6 font-weight-bold">
                      {{ formatCurrency(pendingTotalValue) }}
                    </div>
                  </div>
                  <v-icon size="28" :color="colors.primaryDark">fas fa-dollar-sign</v-icon>
                </div>
              </v-card>
            </v-col>
          </v-row>
        </v-card>

        <!-- Filters -->
        <v-card class="filters-card mb-5" elevation="0">
          <v-row dense align="center">
            <v-col cols="12" md="4">
              <v-text-field
                v-model="search"
                variant="outlined"
                density="comfortable"
                hide-details
                label="Buscar por arquivo ou beneficiário"
                prepend-inner-icon="fas fa-magnifying-glass"
                :color="colors.primaryLight"
              />
            </v-col>

            <v-col cols="12" md="3">
              <v-select
                v-model="statusFilter"
                :items="statusOptions"
                item-title="label"
                item-value="value"
                variant="outlined"
                density="comfortable"
                hide-details
                label="Status"
                :color="colors.primaryLight"
                prepend-inner-icon="fas fa-filter"
              />
            </v-col>

            <v-col cols="12" md="5" class="d-flex justify-end" style="gap: 10px">
              <v-btn
                variant="tonal"
                :color="colors.primaryDark"
                prepend-icon="fas fa-shield"
                class="btn-soft"
                @click="openReauth"
              >
                Reautenticar para agendar
              </v-btn>

              <v-btn
                :color="colors.primaryLight"
                class="btn-primary"
                prepend-icon="fas fa-calendar-plus"
                :disabled="!authVerified || !selectedFile"
                @click="schedulePayment(selectedFile)"
              >
                Agendar selecionado
              </v-btn>
            </v-col>
          </v-row>
        </v-card>

        <!-- Files Grid -->
        <v-card class="files-container" elevation="0">
          <v-card-title class="pa-4 files-title">
            <div class="d-flex align-center justify-space-between w-100">
              <div class="d-flex align-center">
                <v-icon :color="colors.primaryDark" class="mr-2">fas fa-folder-open</v-icon>
                <span class="font-weight-bold">Arquivos de Pagamento</span>
              </div>

              <v-chip size="small" variant="tonal" :color="colors.primaryDark">
                {{ filteredFiles.length }} itens
              </v-chip>
            </div>
          </v-card-title>

          <v-divider />

          <v-card-text class="pa-4">
            <v-row>
              <v-col
                v-for="file in filteredFiles"
                :key="file.id"
                cols="12"
                md="6"
                lg="4"
              >
                <v-card
                  class="file-card"
                  :class="{
                    'selected-card': selectedFile?.id === file.id,
                    'processed-card': file.status === 'processed',
                    'pending-card': file.status === 'pending'
                  }"
                  elevation="0"
                  hover
                  @click="selectFile(file)"
                >
                  <v-card-text class="pa-4">
                    <div class="d-flex align-center mb-3">
                      <v-icon
                        :color="getStatusColor(file.status)"
                        size="34"
                        class="mr-3"
                      >
                        {{ getStatusIcon(file.status) }}
                      </v-icon>

                      <div class="flex-grow-1">
                        <div class="font-weight-bold text-body-1 mb-1 text-truncate">
                          {{ file.name }}
                        </div>

                        <div class="d-flex align-center" style="gap: 8px">
                          <v-chip
                            :color="getStatusColor(file.status)"
                            size="x-small"
                            class="text-caption"
                          >
                            {{ getStatusText(file.status) }}
                          </v-chip>

                          <v-chip
                            v-if="file.scheduledDate"
                            size="x-small"
                            variant="tonal"
                            :color="colors.primaryDark"
                          >
                            Agendado: {{ formatDate(file.scheduledDate) }}
                          </v-chip>
                        </div>
                      </div>
                    </div>

                    <v-divider class="mb-3" />

                    <div class="file-details">
                      <div class="d-flex justify-space-between align-center mb-2">
                        <span class="text-caption text-grey-darken-1">Valor</span>
                        <span class="font-weight-bold" :class="getValueClass(file.value)">
                          {{ formatCurrency(file.value) }}
                        </span>
                      </div>

                      <div class="d-flex justify-space-between align-center mb-2">
                        <span class="text-caption text-grey-darken-1">Vencimento</span>
                        <span class="text-body-2">{{ file.dueDate }}</span>
                      </div>

                      <div class="d-flex justify-space-between align-center">
                        <span class="text-caption text-grey-darken-1">Beneficiário</span>
                        <span class="text-body-2 text-truncate ml-3" style="max-width: 180px">
                          {{ file.beneficiary }}
                        </span>
                      </div>
                    </div>

                    <v-alert
                      v-if="!authVerified && file.status !== 'processed'"
                      type="warning"
                      variant="tonal"
                      density="compact"
                      class="mt-4"
                    >
                      Verificação de senha necessária para agendar.
                    </v-alert>

                    <v-btn
                      v-if="file.status !== 'processed'"
                      block
                      :color="colors.primaryLight"
                      class="mt-4 btn-primary"
                      :disabled="!authVerified || (loading && selectedFile?.id === file.id)"
                      :loading="loading && selectedFile?.id === file.id"
                      @click.stop="schedulePayment(file)"
                    >
                      <v-icon class="mr-2">fas fa-calendar-plus</v-icon>
                      Agendar pagamento
                    </v-btn>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>

            <!-- Empty State -->
            <div v-if="filteredFiles.length === 0" class="text-center py-12">
              <v-icon size="80" color="grey-lighten-1" class="mb-4">
                fas fa-file-pdf
              </v-icon>
              <h3 class="text-h6 text-grey-darken-1 mb-2">Nenhum resultado</h3>
              <p class="text-body-2 text-grey-darken-1">
                Ajuste os filtros ou aguarde novos arquivos.
              </p>
            </div>
          </v-card-text>
        </v-card>

        <!-- Payment Modal -->
        <v-dialog v-model="paymentDialog" max-width="650px" persistent>
          <v-card class="modal-card">
            <v-card-title class="pa-4 modal-title">
              <div class="d-flex align-center justify-space-between w-100">
                <div class="d-flex align-center">
                  <v-icon class="mr-2" color="white">fas fa-calendar-check</v-icon>
                  Confirmar agendamento
                </div>

                <v-chip size="small" color="white" variant="outlined">
                  Sicoob
                </v-chip>
              </div>
            </v-card-title>

            <v-card-text class="pa-6">
              <v-alert
                v-if="!authVerified"
                type="error"
                variant="tonal"
                class="mb-4"
              >
                Você precisa verificar a senha da conta logada antes de agendar.
              </v-alert>

              <div v-if="selectedFile" class="payment-details">
                <v-row>
                  <v-col cols="12">
                    <div class="mb-4 pa-4 bg-blue-grey-lighten-5 rounded-lg">
                      <div class="d-flex align-center justify-space-between mb-3">
                        <h4 class="mb-0">Detalhes</h4>
                        <v-chip
                          size="small"
                          :color="getStatusColor(selectedFile.status)"
                          variant="tonal"
                        >
                          {{ getStatusText(selectedFile.status) }}
                        </v-chip>
                      </div>

                      <div class="d-flex justify-space-between mb-2">
                        <strong>Arquivo</strong>
                        <span class="text-truncate ml-3" style="max-width: 320px">
                          {{ selectedFile.name }}
                        </span>
                      </div>
                      <div class="d-flex justify-space-between mb-2">
                        <strong>Valor</strong>
                        <span class="font-weight-bold text-green-darken-2">
                          {{ formatCurrency(selectedFile.value) }}
                        </span>
                      </div>
                      <div class="d-flex justify-space-between mb-2">
                        <strong>Beneficiário</strong>
                        <span class="text-truncate ml-3" style="max-width: 320px">
                          {{ selectedFile.beneficiary }}
                        </span>
                      </div>
                      <div class="d-flex justify-space-between">
                        <strong>Vencimento</strong>
                        <span>{{ selectedFile.dueDate }}</span>
                      </div>
                    </div>
                  </v-col>

                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="scheduledDate"
                      type="date"
                      label="Data do agendamento"
                      :color="colors.primaryLight"
                      variant="outlined"
                      :min="today"
                      required
                    />
                  </v-col>

                  <v-col cols="12" md="6">
                    <v-select
                      v-model="priority"
                      :items="priorityOptions"
                      label="Prioridade"
                      variant="outlined"
                      density="comfortable"
                      hide-details
                      :color="colors.primaryLight"
                      prepend-inner-icon="fas fa-flag"
                    />
                  </v-col>

                  <v-col cols="12">
                    <v-textarea
                      v-model="paymentNotes"
                      label="Observações (opcional)"
                      :color="colors.primaryLight"
                      variant="outlined"
                      rows="3"
                    />
                  </v-col>
                </v-row>
              </div>
            </v-card-text>

            <v-card-actions class="pa-4">
              <v-spacer />
              <v-btn variant="outlined" @click="paymentDialog = false">Cancelar</v-btn>
              <v-btn
                :color="colors.primaryLight"
                class="btn-primary"
                :loading="loading"
                :disabled="!authVerified || !scheduledDate"
                @click="confirmPayment"
              >
                Confirmar
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <!-- Re-auth Overlay (Senha do usuário logado) -->
        <v-overlay
          v-model="reauthOverlay"
          class="d-flex align-center justify-center"
          persistent
          :z-index="3000"
        >
          <v-card class="reauth-card" elevation="12" rounded="xl">
            <v-card-title class="reauth-title">
              <div class="d-flex align-center justify-space-between w-100">
                <div class="d-flex align-center">
                  <v-icon class="mr-2" color="white">fas fa-lock</v-icon>
                  Verificação de segurança
                </div>
                <v-chip size="small" color="white" variant="outlined">
                  Conta logada
                </v-chip>
              </div>
            </v-card-title>

            <v-card-text class="pa-6">
              <p class="text-body-2 text-grey-darken-1 mb-4">
                Para agendar pagamentos no Sicoob, confirme a senha do seu usuário.
              </p>

              <v-alert
                v-if="reauthError"
                type="error"
                variant="tonal"
                class="mb-4"
              >
                {{ reauthError }}
              </v-alert>

              <v-text-field
                v-model="reauthPassword"
                :type="reauthShow ? 'text' : 'password'"
                label="Senha"
                variant="outlined"
                :color="colors.primaryLight"
                prepend-inner-icon="fas fa-key"
                :append-inner-icon="reauthShow ? 'fas fa-eye-slash' : 'fas fa-eye'"
                @click:append-inner="reauthShow = !reauthShow"
                @keyup.enter="confirmReauth"
                :disabled="reauthLoading"
              />

              <div class="text-caption text-grey-darken-1 mt-2">
                Dica: essa verificação pode expirar após alguns minutos (padrão recomendado: 10–15 min).
              </div>
            </v-card-text>

            <v-card-actions class="pa-4">
              <v-spacer />
              <v-btn variant="outlined" :disabled="reauthLoading" @click="reauthOverlay = false">
                Agora não
              </v-btn>
              <v-btn
                :color="colors.primaryLight"
                class="btn-primary"
                :loading="reauthLoading"
                :disabled="!reauthPassword"
                @click="confirmReauth"
              >
                Confirmar senha
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-overlay>

        <!-- Success Snackbar -->
        <v-snackbar v-model="successSnackbar" :color="snackbarColor" timeout="4000" top>
          <v-icon class="mr-2">fas fa-check-circle</v-icon>
          {{ successMessage }}
        </v-snackbar>
      </div>
    </v-main>
  </v-app>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from "vue";
import { useRouter } from "vue-router";
import axios from "axios";
import { useAuthStore } from "@/stores/auth";
import { menuItems } from "@/constants/MenuItems";

const API_BASE = (process.env.VUE_APP_API_URL || "").replace(/\/$/, "");
const FINANCER_FILES_ENDPOINT = process.env.VUE_APP_FINANCER_FILES_ENDPOINT || "/financer/files";
const FINANCER_SCHEDULE_ENDPOINT = process.env.VUE_APP_FINANCER_SCHEDULE_ENDPOINT || "/financer/schedule";
const AUTH_RECONFIRM_ENDPOINT = process.env.VUE_APP_AUTH_RECONFIRM_ENDPOINT || "/auth/reconfirm";

const colors = {
  primaryDark: "#123A5D",
  primaryLight: "#57C2E3",
  primaryGreen: "#A3C940",
  lightBg: "#E7F2F9",
};

const router = useRouter();
const authStore = useAuthStore();
const drawer = ref(true);
const userDetails = ref({});
const isMounted = ref(false);
const token = computed(() => authStore?.token || localStorage.getItem("authToken") || null);

// Re-auth (Senha do usuário logado)
const authVerified = ref(false);
const reauthOverlay = ref(false);
const reauthPassword = ref("");
const reauthShow = ref(false);
const reauthLoading = ref(false);
const reauthError = ref("");

// TTL simples no front (evita ficar “verificado” pra sempre)
const authVerifiedUntil = ref(0);
const AUTH_TTL_MS = 10 * 60 * 1000; // 10 minutos

const openReauth = () => {
  reauthError.value = "";
  reauthPassword.value = "";
  reauthShow.value = false;
  reauthOverlay.value = true;
};

// Payment scheduler
const selectedFile = ref(null);
const paymentDialog = ref(false);
const loading = ref(false);
const successSnackbar = ref(false);
const successMessage = ref("");
const snackbarColor = ref(colors.primaryGreen);
const scheduledDate = ref("");
const paymentNotes = ref("");
const priority = ref("Normal");

const priorityOptions = ["Baixa", "Normal", "Alta"];

// Filters
const search = ref("");
const statusFilter = ref("all");
const statusOptions = [
  { label: "Todos", value: "all" },
  { label: "Pendentes", value: "pending" },
  { label: "Processados", value: "processed" },
];

const pdfFiles = ref([]);

const buildUrl = (endpoint) => {
  if (!endpoint) return API_BASE;
  if (/^https?:\/\//i.test(endpoint)) return endpoint;
  return `${API_BASE}${endpoint.startsWith("/") ? endpoint : `/${endpoint}`}`;
};

const authHeaders = computed(() => (
  token.value ? { Authorization: `Bearer ${token.value}` } : {}
));

const openSnackbar = (message, color = colors.primaryGreen) => {
  successMessage.value = message;
  snackbarColor.value = color;
  successSnackbar.value = true;
};

const normalizeStatus = (status) => {
  const normalized = String(status || "").toLowerCase();
  if (
    normalized.includes("processed")
    || normalized.includes("processado")
    || normalized.includes("scheduled")
    || normalized.includes("agendado")
    || normalized.includes("done")
    || normalized.includes("completed")
  ) {
    return "processed";
  }
  return "pending";
};

const toDisplayDate = (dateValue) => {
  if (!dateValue) return "Não informado";
  if (typeof dateValue === "string" && /^\d{2}\/\d{2}\/\d{4}$/.test(dateValue)) {
    return dateValue;
  }

  const parsedDate = new Date(dateValue);
  if (Number.isNaN(parsedDate.getTime())) {
    return String(dateValue);
  }
  return parsedDate.toLocaleDateString("pt-BR");
};

const normalizeFile = (file, index) => ({
  id: file?.id ?? file?.fileId ?? index + 1,
  name: file?.name || file?.fileName || file?.filename || `Arquivo ${index + 1}`,
  value: Number(file?.value ?? file?.amount ?? 0) || 0,
  dueDate: toDisplayDate(file?.dueDate ?? file?.due_date ?? file?.expirationDate),
  beneficiary: file?.beneficiary || file?.payee || file?.receiver || "Não informado",
  status: normalizeStatus(file?.status),
  scheduledDate: file?.scheduledDate || file?.scheduled_date || file?.scheduledAt || null,
});

const filteredFiles = computed(() => {
  const s = search.value.trim().toLowerCase();
  return pdfFiles.value.filter((f) => {
    const matchesSearch =
      !s ||
      f.name.toLowerCase().includes(s) ||
      f.beneficiary.toLowerCase().includes(s);

    const matchesStatus =
      statusFilter.value === "all" ? true : f.status === statusFilter.value;

    return matchesSearch && matchesStatus;
  });
});

// KPIs
const pendingCount = computed(() => pdfFiles.value.filter((f) => f.status === "pending").length);
const processedCount = computed(() => pdfFiles.value.filter((f) => f.status === "processed").length);
const pendingTotalValue = computed(() =>
  pdfFiles.value
    .filter((f) => f.status === "pending")
    .reduce((acc, f) => acc + (Number(f.value) || 0), 0)
);

const today = computed(() => new Date().toISOString().split("T")[0]);

const fetchUserDetails = async () => {
  if (!isMounted.value || !token.value) return;

  try {
    const response = await axios.get(`${API_BASE}/me`, {
      headers: authHeaders.value
    });
    if (isMounted.value) userDetails.value = response.data || {};
  } catch (error) {
    if (isMounted.value) userDetails.value = null;
    console.error("Erro ao buscar dados do usuário:", error);
  }
};

const fetchPaymentFiles = async ({ showFeedback = false } = {}) => {
  if (!token.value) {
    pdfFiles.value = [];
    return;
  }

  try {
    const response = await axios.get(buildUrl(FINANCER_FILES_ENDPOINT), {
      headers: authHeaders.value
    });
    const files = Array.isArray(response.data)
      ? response.data
      : response.data?.items || response.data?.files || [];

    pdfFiles.value = files.map((file, index) => normalizeFile(file, index));
    if (showFeedback) {
      openSnackbar("Lista atualizada com sucesso.");
    }
  } catch (error) {
    console.error("Erro ao carregar arquivos de pagamento:", error);
    pdfFiles.value = [];
    if (showFeedback) {
      openSnackbar("Não foi possível atualizar a lista de arquivos.", "error");
    }
  }
};

const logout = () => {
  if (authStore && typeof authStore.logout === "function") {
    authStore.logout();
  }
  router.push("/");
};

// Helpers
const selectFile = (file) => {
  selectedFile.value = file;
};

const ensureAuthStillValid = () => {
  if (!authVerified.value) return false;
  if (Date.now() > authVerifiedUntil.value) {
    authVerified.value = false;
    return false;
  }
  return true;
};

// Open schedule modal
const schedulePayment = (file) => {
  selectedFile.value = file;

  if (!ensureAuthStillValid()) {
    openReauth();
    return;
  }

  scheduledDate.value = "";
  paymentNotes.value = "";
  priority.value = "Normal";
  paymentDialog.value = true;
};

// Confirm schedule
const confirmPayment = async () => {
  if (!scheduledDate.value) return;

  if (!ensureAuthStillValid()) {
    paymentDialog.value = false;
    openReauth();
    return;
  }

  loading.value = true;
  try {
    await schedulePaymentRequest(selectedFile.value, scheduledDate.value, paymentNotes.value, priority.value);

    const idx = pdfFiles.value.findIndex((f) => f.id === selectedFile.value.id);
    if (idx !== -1) {
      pdfFiles.value[idx].status = "processed";
      pdfFiles.value[idx].scheduledDate = scheduledDate.value;
    }

    openSnackbar(`Pagamento agendado com sucesso para ${formatDate(scheduledDate.value)}.`);
    paymentDialog.value = false;
  } catch (error) {
    console.error("Erro ao agendar pagamento:", error);
    openSnackbar("Não foi possível agendar o pagamento.", "error");
  } finally {
    loading.value = false;
  }
};

// Reauth flow
const verifyPassword = async (password) => {
  if (!token.value) return false;

  try {
    const response = await axios.post(
      buildUrl(AUTH_RECONFIRM_ENDPOINT),
      { password },
      { headers: authHeaders.value }
    );

    if (typeof response.data?.valid === "boolean") {
      return response.data.valid;
    }
    return response.status >= 200 && response.status < 300;
  } catch (error) {
    if (error?.response?.status === 400 || error?.response?.status === 401) {
      return false;
    }
    throw error;
  }
};

const confirmReauth = async () => {
  reauthError.value = "";
  reauthLoading.value = true;

  try {
    const ok = await verifyPassword(reauthPassword.value);
    if (!ok) {
      reauthError.value = "Senha inválida. Tente novamente.";
      return;
    }

    authVerified.value = true;
    authVerifiedUntil.value = Date.now() + AUTH_TTL_MS;
    reauthOverlay.value = false;

    openSnackbar("Sessão verificada com sucesso. Você pode agendar pagamentos.");
  } catch {
    reauthError.value = "Falha ao verificar senha. Verifique sua conexão e tente novamente.";
  } finally {
    reauthLoading.value = false;
  }
};

const schedulePaymentRequest = async (file, date, notes, prio) => {
  const payload = {
    fileId: file?.id,
    scheduledDate: date,
    notes,
    priority: prio,
    bank: "SICOOB",
  };

  await axios.post(buildUrl(FINANCER_SCHEDULE_ENDPOINT), payload, {
    headers: authHeaders.value
  });
};

const refreshFiles = async () => {
  await fetchPaymentFiles({ showFeedback: true });
};

const formatCurrency = (value) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(Number(value) || 0);

const formatDate = (dateString) => {
  if (!dateString) return "-";
  if (typeof dateString === "string" && /^\d{2}\/\d{2}\/\d{4}$/.test(dateString)) {
    return dateString;
  }

  const parsedDate = new Date(dateString);
  if (Number.isNaN(parsedDate.getTime())) {
    return String(dateString);
  }
  return parsedDate.toLocaleDateString("pt-BR");
};

const getStatusColor = (status) => (status === "processed" ? colors.primaryGreen : status === "pending" ? "orange" : "grey");
const getStatusIcon = (status) => (status === "processed" ? "fas fa-check-circle" : status === "pending" ? "fas fa-clock" : "fas fa-file-pdf");
const getStatusText = (status) => (status === "processed" ? "Processado" : status === "pending" ? "Pendente" : "Aguardando");
const getValueClass = (value) => (value > 100000 ? "text-red-darken-2" : value > 50000 ? "text-orange-darken-2" : "text-green-darken-2");

onMounted(async () => {
  isMounted.value = true;
  await Promise.all([
    fetchUserDetails(),
    fetchPaymentFiles(),
  ]);
});

onBeforeUnmount(() => {
  isMounted.value = false;
});
</script>

<style scoped>
@keyframes pulse {
  0%, 100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.05);
    opacity: 0.8;
  }
}

.gradient-text {
  background: linear-gradient(45deg, #57C2E3, #A3C940);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.pulse {
  animation: pulse 2s infinite;
}

.main-content {
  background: linear-gradient(135deg, #f5f7fa 0%, #e8f4f8 100%);
  padding-top: 18px;
}

.custom-drawer {
  background: linear-gradient(180deg, #123a5d 0%, #0f2a42 100%);
}

.user-profile {
  transition: all 0.3s ease;
  border-radius: 12px;
  padding: 8px 12px;
}
.user-profile:hover {
  background: rgba(255, 255, 255, 0.1);
  cursor: pointer;
}

.profile-avatar {
  border: 2px solid rgba(255, 255, 255, 0.3);
  transition: all 0.3s ease;
}

.profile-avatar:hover {
  border-color: rgba(255, 255, 255, 0.6);
  transform: scale(1.05);
}

.lowercase {
  text-transform: lowercase;
}

.payment-scheduler {
  max-width: 1400px;
  margin: 0 auto;
}

.primary-text {
  color: #123a5d;
}

.module-header {
  border-radius: 16px;
  padding: 18px;
  border: 1px solid rgba(18, 58, 93, 0.12);
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(6px);
}

.kpi-card {
  border-radius: 14px;
  padding: 14px;
  border: 1px solid rgba(18, 58, 93, 0.10);
  background: white;
}

.filters-card {
  border-radius: 16px;
  padding: 14px;
  border: 1px solid rgba(18, 58, 93, 0.10);
  background: white;
}

.files-container {
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid rgba(18, 58, 93, 0.10);
  background: white;
}

.files-title {
  background: linear-gradient(180deg, rgba(18, 58, 93, 0.04), rgba(18, 58, 93, 0.02));
}

.file-card {
  border-radius: 14px;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  border: 1px solid rgba(18, 58, 93, 0.08);
  background: white;
}
.file-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 10px 22px rgba(0, 0, 0, 0.08);
}

.selected-card {
  border: 2px solid #57c2e3;
  box-shadow: 0 10px 22px rgba(87, 194, 227, 0.22);
}

.processed-card {
  background: linear-gradient(135deg, rgba(163, 201, 64, 0.10), rgba(163, 201, 64, 0.03));
}

.pending-card {
  background: linear-gradient(135deg, rgba(255, 152, 0, 0.10), rgba(255, 152, 0, 0.03));
}

.file-details {
  background: rgba(18, 58, 93, 0.03);
  border-radius: 10px;
  padding: 12px;
}

.btn-primary {
  border-radius: 12px;
  font-weight: 700;
  text-transform: none;
  letter-spacing: 0.2px;
}

.btn-soft {
  border-radius: 12px;
  text-transform: none;
}

.logout-btn {
  border-color: rgba(255, 255, 255, 0.25);
  color: rgba(255, 255, 255, 0.85);
}

.modal-card {
  border-radius: 16px;
  overflow: hidden;
}
.modal-title {
  background: #123a5d;
  color: white;
}

.reauth-card {
  width: min(520px, 92vw);
  border-radius: 18px;
  overflow: hidden;
}
.reauth-title {
  background: #123a5d;
  color: white;
  padding: 16px 18px;
}

</style>
